import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTY_MESSAGES, SEND_MESSAGE } from '../../graphql/messages';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { useAuth } from '../../shared/contexts/auth-context';

const ROLE_COLORS = {
    MANAGEMENT: 'bg-purple-100 text-purple-700',
    ADMIN: 'bg-blue-100 text-blue-700',
    AGENT: 'bg-green-100 text-green-700'
};

const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleString('en-GB', {
            day: 'numeric', month: 'short',
            hour: '2-digit', minute: '2-digit'
        });
    } catch {
        return '';
    }
};

const PropertyMessagingTab = () => {
    const { id: propertyId } = useParams();
    const permissions = usePermissions();
    const { user } = useAuth();
    const currentUserId = user?.user?.id || user?.user?._id || user?.id;

    const [messageText, setMessageText] = useState('');
    const bottomRef = useRef(null);

    const { loading, error, data, refetch } = useQuery(GET_PROPERTY_MESSAGES, {
        variables: { propertyId },
        fetchPolicy: 'network-only',
        pollInterval: 30000
    });

    const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE, {
        onCompleted: () => { setMessageText(''); refetch(); },
        onError: err => alert('Error sending message: ' + err.message)
    });

    const messages = data?.propertyMessages || [];

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    const handleSend = (e) => {
        e.preventDefault();
        if (!messageText.trim()) return;
        sendMessage({ variables: { propertyId, content: messageText } });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(e);
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center h-32">
            <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-600 rounded-full border-t-transparent" />
        </div>
    );

    if (error) return (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Failed to load messages: {error.message}
        </div>
    );

    return (
        <div className="bg-white shadow sm:rounded-lg flex flex-col" style={{ minHeight: '400px' }}>
            <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-700">Team Messaging</h3>
                <p className="text-xs text-gray-400 mt-0.5">Internal thread — visible to management and admin only</p>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '480px' }}>
                {messages.length === 0 ? (
                    <div className="text-center py-8">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <p className="text-sm text-gray-500">No messages yet. Start the conversation.</p>
                    </div>
                ) : (
                    messages.map(msg => {
                        const isOwn = msg.senderId === currentUserId?.toString();
                        return (
                            <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-xs lg:max-w-md ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-medium text-gray-700">{msg.senderName}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ROLE_COLORS[msg.senderRole] || 'bg-gray-100 text-gray-600'}`}>
                                            {msg.senderRole}
                                        </span>
                                        <span className="text-xs text-gray-400">{formatTime(msg.createdAt)}</span>
                                    </div>
                                    <div className={`px-3 py-2 rounded-lg text-sm ${isOwn ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                                        <p className="whitespace-pre-wrap">{msg.content}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input area */}
            <div className="border-t border-gray-200 p-4">
                {permissions.canSendMessages ? (
                    <form onSubmit={handleSend} className="flex gap-3">
                        <textarea
                            value={messageText}
                            onChange={e => setMessageText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                            rows={2}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                        />
                        <button
                            type="submit"
                            disabled={!messageText.trim() || sending}
                            className="self-end inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                            {sending ? 'Sending…' : 'Send'}
                        </button>
                    </form>
                ) : (
                    <p className="text-sm text-gray-400 text-center">You do not have permission to send messages.</p>
                )}
            </div>
        </div>
    );
};

export default PropertyMessagingTab;
